import { useMemo } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import {
  primaryColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import DatePickerField from 'components/DateRange/DatePickerField/DatePickerField';

const useStyles = makeStyles({
  picker: {
    maxWidth: '240px',
    margin: '0 auto',
    '& input.form-control': {
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 400,
      textAlign: 'center',
    },
    '& .rdtPicker': {
      left: '50%',
      right: 'auto',
      transform: 'translateX(-50%)',
    },
    '& .rdtPicker:before, & .rdtPicker:after': {
      left: '50%',
      right: 'auto',
      transform: 'translateX(-50%)',
    },
    '& .rdtPicker .rdtDays tbody td': {
      padding: 0,
    },
    '& .rdtPicker td.rdtDay:hover, & .rdtPicker td.rdtActive, & .rdtPicker td.rdtActive:hover':
      {
        background: 'transparent !important',
        color: 'inherit',
      },
    '& .rdtPicker td.rdtActive.rdtToday:before': {
      borderBottomColor: 'transparent',
    },
    '& .rdtPicker .rdtDays tbody tr:hover $weekPickerDay': {
      background: '#eeeeee',
    },
    '& .rdtPicker .rdtDays tbody tr:hover $weekPickerDaySelected': {
      background: primaryColor[0],
      color: whiteColor,
    },
  },
  weekPickerDay: {
    width: '100%',
    height: '30px',
    lineHeight: '30px',
    borderRadius: 0,
    transition: 'background-color 0.2s ease',
  },
  weekPickerDayStart: {
    borderTopLeftRadius: 999,
    borderBottomLeftRadius: 999,
  },
  weekPickerDayEnd: {
    borderTopRightRadius: 999,
    borderBottomRightRadius: 999,
  },
  weekPickerDaySelected: {
    background: primaryColor[0],
    color: whiteColor,
  },
});

const getWeekStart = value => moment(value).startOf('isoWeek');

const formatWeekRange = weekStart => {
  if (!weekStart) {
    return '';
  }

  const normalizedWeekStart = getWeekStart(weekStart);
  const weekEnd = normalizedWeekStart.clone().endOf('isoWeek');

  if (normalizedWeekStart.isSame(weekEnd, 'month')) {
    return `${normalizedWeekStart.format('DD')}-${weekEnd.format(
      'DD'
    )} ${weekEnd.format('MMMM YYYY')}`;
  }

  if (normalizedWeekStart.isSame(weekEnd, 'year')) {
    return `${normalizedWeekStart.format('DD MMMM')} - ${weekEnd.format(
      'DD MMMM YYYY'
    )}`;
  }

  return `${normalizedWeekStart.format('DD MMMM YYYY')} - ${weekEnd.format(
    'DD MMMM YYYY'
  )}`;
};

const MenuSummaryWeekPicker = ({ value, onChange, locale }) => {
  const classes = useStyles();

  const pickerLocale = useMemo(() => {
    const localeName = locale || moment.locale();
    const activeGlobalLocale = moment.locale();
    moment.updateLocale(localeName, { week: { dow: 1 } });
    moment.locale(activeGlobalLocale);
    return localeName;
  }, [locale]);

  const selectedWeekStart = value ? getWeekStart(value) : null;
  const displayValue = formatWeekRange(selectedWeekStart);

  return (
    <DatePickerField
      className={classes.picker}
      closeOnSelect={true}
      dateFormat={moment.localeData().longDateFormat('L')}
      inputProps={{
        'aria-label': displayValue,
      }}
      isValidDate={() => true}
      label={null}
      locale={pickerLocale}
      onChange={(_valueString, MomentInstance) => {
        onChange(getWeekStart(MomentInstance));
      }}
      renderDay={(props, currentDate) => {
        const weekPickerDayClasses = [classes.weekPickerDay];

        if (
          selectedWeekStart &&
          currentDate.isSame(selectedWeekStart, 'isoWeek')
        ) {
          weekPickerDayClasses.push(classes.weekPickerDaySelected);
        }

        if (currentDate.isoWeekday() === 1) {
          weekPickerDayClasses.push(classes.weekPickerDayStart);
        }

        if (currentDate.isoWeekday() === 7) {
          weekPickerDayClasses.push(classes.weekPickerDayEnd);
        }

        return (
          <td {...props}>
            <div className={weekPickerDayClasses.join(' ')}>
              {currentDate.date()}
            </div>
          </td>
        );
      }}
      renderInput={inputProps => (
        <input {...inputProps} readOnly={true} value={displayValue} />
      )}
      value={selectedWeekStart}
    />
  );
};

export default MenuSummaryWeekPicker;
